import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";
import { packWindowMessage } from "/imports/libs/packWindowMessage";
import api from "/imports/api/client";
import { saveResumeIntent } from "/imports/libs/resumeIntent";
import { collectWeeklySlots, weeklyRepeatMessage } from "/imports/libs/weeklyRepeat";
import { groupSlotsByTime, getRecommendedSlot } from "/imports/ui/components/booking";
import { formatExpiry } from "../packagesHelpers";
import { wall, windowEnd } from "./upfrontSlotsHelpers";

/**
 * Session builder for upfront-booked PRIVATE LESSON / COURT packages: there is
 * no timetable to tick, so the client composes each session — pick a day and a
 * free time (plus a trainer for lessons; the court resolves server-side) — in
 * a bottom-sheet slot composer fed by the same availability the normal booking
 * flows render. The composed slots are held server-side while paying.
 */
function useUpfrontSlots({ pkg, opt, locationId, backTo }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, locations } = useWidget();

  const isCourt = opt.linkedCategoryType === "category-service-court";
  const mode = opt.upfrontBooking;
  const sessionsN = opt.sessions || 0;
  const anchor = opt.upfrontAnchor === "saleDate" ? "saleDate" : "firstSession";
  const minPicks = mode === "all" ? sessionsN : 1;
  const windowCfg = !opt.noExpiry && opt.expiryDuration && opt.expiryDurationUnit ? { dur: opt.expiryDuration, unit: opt.expiryDurationUnit } : null;

  // Where the sessions are composed: the purchase location, or (shared packs
  // sold without one) a location the pack covers.
  const composeLocationIds = useMemo(() => {
    if (locationId) return [locationId];
    const ids = pkg?.locationIds || [];
    return ids.length > 0 ? ids : (locations || []).map((l) => l._id);
  }, [locationId, pkg, locations]);
  const [composeLocationId, setComposeLocationId] = useState(composeLocationIds[0] || null);
  const locationNameById = useMemo(() => new Map((locations || []).map((l) => [l._id, l.name])), [locations]);

  // Composed picks, kept sorted by start.
  const [picks, setPicks] = useState([]);

  // Composer sheet state. sheetTrainerId: null = "Any trainer" (lessons only);
  // seeded with the previous pick's trainer for continuity.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDate, setSheetDate] = useState(() => DateTime.now().toISODate());
  const [sheetTrainerId, setSheetTrainerId] = useState(null);
  const [daySlots, setDaySlots] = useState([]);
  const [dayTrainers, setDayTrainers] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [repeatBusy, setRepeatBusy] = useState(false);
  // Weeks the repeat stepped over because the trainer/court was taken — a UI
  // record of what the server never offered, not slots.
  const [blockedWeeks, setBlockedWeeks] = useState([]);

  const today = useMemo(() => DateTime.now().startOf("day"), []);

  // Date strip cap: saleDate anchor can't book past the pack's deadline from
  // today; firstSession is bounded by the earliest pick once one exists.
  const maxDate = useMemo(() => {
    if (windowCfg && anchor === "saleDate") return windowEnd(today, windowCfg.dur, windowCfg.unit).toISODate();
    if (windowCfg && anchor === "firstSession" && picks.length > 0) {
      return windowEnd(wall(picks[0].startDate).startOf("day"), windowCfg.dur, windowCfg.unit).toISODate();
    }
    return today.plus({ days: 180 }).toISODate();
  }, [windowCfg, anchor, picks, today]);

  // Once a first session is picked, the pack's own validity — not the club's
  // public booking window — bounds the rest. Sent on every availability call so
  // the server, which re-resolves the same deadline from the product, opens the
  // days the pack covers. See UpfrontWindowService.
  const packAvailabilityParams = {
    packProduct: pkg._id,
    packVariant: opt.variantKey,
    ...(picks.length > 0 ? { packFirstDate: picks[0].date } : {})
  };

  const fetchDay = async (dateISO, trainerId) => {
    const params = {
      date: dateISO,
      serviceId: opt.linkedServiceId,
      duration: opt.sessionDuration,
      locationId: composeLocationId || undefined,
      ...packAvailabilityParams
    };
    return isCourt ? api.getCourtAvailability(slug, params) : api.getLessonAvailability(slug, { ...params, trainerId: trainerId || undefined });
  };

  useEffect(() => {
    if (!sheetOpen) return;
    let stale = false;
    setSlotsLoading(true);
    fetchDay(sheetDate, sheetTrainerId)
      .then((res) => {
        if (stale) return;
        setDaySlots(res?.slots || []);
        if (!isCourt) setDayTrainers(res?.trainers || []);
      })
      .catch(() => {
        if (!stale) setDaySlots([]);
      })
      .finally(() => {
        if (!stale) setSlotsLoading(false);
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen, sheetDate, sheetTrainerId, composeLocationId]);

  // Times already picked on the sheet's day can't be picked twice.
  const pickedTimes = useMemo(() => new Set(picks.filter((p) => p.date === sheetDate).map((p) => p.time)), [picks, sheetDate]);
  const groups = useMemo(() => groupSlotsByTime(daySlots), [daySlots]);

  // Would adding a session on this day keep every pick inside one validity
  // window? (firstSession anchor: an EARLIER pick moves the window start back.)
  const fitsWindow = (dateISO) => {
    if (!windowCfg || anchor !== "firstSession" || picks.length === 0) return true;
    const d = DateTime.fromISO(dateISO, { zone: "utc" });
    const first = wall(picks[0].startDate).startOf("day");
    const last = wall(picks[picks.length - 1].startDate);
    const start = d < first ? d.startOf("day") : first;
    const end = d.endOf("day") > last ? d.endOf("day") : last;
    return end <= windowEnd(start, windowCfg.dur, windowCfg.unit);
  };

  const byStart = (a, b) => (a.startDate < b.startDate ? -1 : 1);

  const addPick = (group) => {
    const slot = !isCourt && sheetTrainerId ? group.slots.find((s) => s.trainerId === sheetTrainerId) || group.slots[0] : getRecommendedSlot(group);
    if (!slot) return;
    if (picks.length >= sessionsN) {
      enqueueSnackbar(`This pack covers ${sessionsN} sessions — remove one first`, { variant: "info" });
      return;
    }
    const key = `${sheetDate}|${slot.time}`;
    if (picks.some((p) => p.key === key)) {
      enqueueSnackbar("You already picked that time", { variant: "info" });
      return;
    }
    if (!fitsWindow(sheetDate)) {
      enqueueSnackbar(
        packWindowMessage({
          firstISO: picks[0].startDate,
          deadline: windowEnd(wall(picks[0].startDate).startOf("day"), windowCfg.dur, windowCfg.unit),
          dur: windowCfg.dur,
          unit: windowCfg.unit,
          noun: isCourt ? "session" : "lesson"
        }),
        { variant: "warning" }
      );
      return;
    }
    setPicks((prev) =>
      [
        ...prev,
        {
          key,
          date: sheetDate,
          time: slot.time,
          startDate: slot.startDate,
          trainerId: slot.trainerId || null,
          trainerName: slot.trainerName || null,
          facilityId: slot.facilityId || null,
          facilityName: slot.facilityName || null,
          locationId: slot.locationId,
          locationName: slot.locationName || locationNameById.get(slot.locationId) || null
        }
      ].sort(byStart)
    );
    setBlockedWeeks((prev) => prev.filter((b) => b.date !== sheetDate));
    setSheetOpen(false);
  };

  const removePick = (key) => setPicks((prev) => prev.filter((p) => p.key !== key));

  const clearPicks = () => {
    setPicks([]);
    setBlockedWeeks([]);
  };

  const dismissBlockedWeek = (date) => setBlockedWeeks((prev) => prev.filter((b) => b.date !== date));

  // Tapping a blocked week opens the composer on that day, so the client sees
  // for themselves that the time is gone and takes another one. The placeholder
  // is spent once tapped: it has done its job of being noticed, and Continue
  // goes back to whatever the pack's own rule says.
  const openBlockedWeek = (week) => {
    dismissBlockedWeek(week.date);
    if (!isCourt) setSheetTrainerId(week.trainerId || null);
    setSheetDate(week.date);
    setSheetOpen(true);
    enqueueSnackbar(`${week.trainerName || "That slot"} isn't free at ${week.time} on ${wall(week.date).toFormat("ccc d LLL")} — pick another time`, {
      variant: "warning"
    });
  };

  const openComposer = () => {
    // Trainer continuity: start the next pick on the previous session's coach.
    const lastPick = picks[picks.length - 1];
    if (!isCourt) setSheetTrainerId(lastPick?.trainerId || null);
    setSheetDate((cur) => {
      const max = maxDate;
      return cur > max ? today.toISODate() : cur;
    });
    setSheetOpen(true);
  };

  // Fill the remaining sessions with the same weekday/time (and trainer/court
  // preference) week after week, checking real availability and stepping over
  // the weeks that are taken.
  const repeatWeekly = async () => {
    const last = picks[picks.length - 1];
    if (!last || picks.length >= sessionsN) return;
    setRepeatBusy(true);

    const deadline = windowCfg
      ? windowEnd(anchor === "firstSession" ? wall(picks[0].startDate).startOf("day") : today, windowCfg.dur, windowCfg.unit)
      : null;

    const findSlot = async (dateISO) => {
      const res = await fetchDay(dateISO, isCourt ? null : last.trainerId);
      const slots = res?.slots || [];
      return (
        slots.find((s) => s.time === last.time && (isCourt ? s.facilityId === last.facilityId : s.trainerId === last.trainerId)) ||
        slots.find((s) => s.time === last.time) ||
        null
      );
    };

    const { matches, skippedDates } = await collectWeeklySlots({
      fromDate: last.date,
      deadline,
      need: sessionsN - picks.length,
      findSlot
    });
    setRepeatBusy(false);

    if (matches.length === 0) {
      enqueueSnackbar("No matching weekly slots found ahead", { variant: "info" });
      return;
    }
    const added = matches.map(({ date, slot }) => ({
      key: `${date}|${slot.time}`,
      date,
      time: slot.time,
      startDate: slot.startDate,
      trainerId: slot.trainerId || null,
      trainerName: slot.trainerName || null,
      facilityId: slot.facilityId || null,
      facilityName: slot.facilityName || null,
      locationId: slot.locationId,
      locationName: slot.locationName || locationNameById.get(slot.locationId) || null
    }));
    setPicks((prev) => [...prev, ...added].sort(byStart));
    setBlockedWeeks(
      skippedDates.map((date) => ({
        date,
        time: last.time,
        trainerId: last.trainerId || null,
        trainerName: isCourt ? last.facilityName : last.trainerName
      }))
    );
    const remaining = sessionsN - picks.length - added.length;
    enqueueSnackbar(weeklyRepeatMessage({ addedN: added.length, skipped: skippedDates.length, remaining, unit: isCourt ? "session" : "lesson" }), {
      variant: remaining > 0 ? "info" : "success"
    });
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  const doPurchase = async ({ voucherId } = {}) => {
    setBuying(true);
    try {
      const result = await api.purchasePackage(slug, {
        productId: pkg._id,
        pricingId: opt.pricingId,
        variantKey: opt.variantKey,
        sessions: opt.sessions,
        sessionDuration: opt.sessionDuration,
        linkedServiceId: opt.linkedServiceId,
        locationId: locationId || undefined,
        upfrontSlots: picks.map((p) => ({
          date: p.date,
          time: p.time,
          trainerId: p.trainerId || undefined,
          facilityId: p.facilityId || undefined,
          locationId: p.locationId
        })),
        voucherId: voucherId || undefined
      });
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        if (backTo) saveResumeIntent({ slug, backTo });
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete — your sessions are booked", { variant: "success" });
      navigate(`/${slug}/dashboard?tab=bookings&scope=upcoming`);
    } catch (err) {
      setConfirmOpen(false);
      // A pick that was just taken comes back with its "date|time" key — drop
      // it so the client replaces exactly that one.
      const conflictKey = err.data?.data?.slotKey;
      if (conflictKey) removePick(conflictKey);
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuying(false);
    }
  };

  const countOk = picks.length >= minPicks && picks.length <= sessionsN;
  // Payment waits on every hole being cleared off the list — by opening it, or
  // by dismissing it. Either way the client has seen the week that could not be
  // booked, and from there the pack's own rule decides: an `all` pack still
  // wants its full count, a `partial` one is free to go.
  const canContinue = countOk && blockedWeeks.length === 0;
  const noun = isCourt ? "court session" : "lesson";

  const confirmLines = [
    [
      opt.sessions ? `${opt.sessions} session${opt.sessions === 1 ? "" : "s"}` : "Package",
      opt.sessionDuration ? `${opt.sessionDuration} min each` : null,
      formatExpiry(opt)
    ]
      .filter(Boolean)
      .join(" · "),
    `${picks.length} session${picks.length === 1 ? "" : "s"} booked now${
      mode === "partial" && picks.length < sessionsN ? ` — ${sessionsN - picks.length} left to book later (subject to availability)` : ""
    }`,
    picks.length > 0 ? `${wall(picks[0].startDate).toFormat("d LLL")} – ${wall(picks[picks.length - 1].startDate).toFormat("d LLL yyyy")}` : null
  ].filter(Boolean);

  const confirmNote = anchor === "firstSession" && !opt.noExpiry ? "Your pack's validity starts at your first session, not today." : null;

  return {
    navigate,
    slug,
    currency,
    isCourt,
    noun,
    mode,
    sessionsN,
    minPicks,
    countOk,
    canContinue,
    composeLocationIds,
    composeLocationId,
    setComposeLocationId,
    locationNameById,
    picks,
    setPicks,
    blockedWeeks,
    removePick,
    clearPicks,
    dismissBlockedWeek,
    openBlockedWeek,
    addPick,
    openComposer,
    sheetOpen,
    setSheetOpen,
    sheetDate,
    setSheetDate,
    sheetTrainerId,
    setSheetTrainerId,
    today,
    maxDate,
    dayTrainers,
    slotsLoading,
    groups,
    pickedTimes,
    repeatBusy,
    repeatWeekly,
    confirmOpen,
    setConfirmOpen,
    buying,
    doPurchase,
    confirmLines,
    confirmNote
  };
}

export default useUpfrontSlots;
